import React from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../shared';
import { useApp, useAuth } from '../../hooks';
import { SUBSCRIPTION_PLANS } from '../../constants';
import UpgradePlans from './Plan/UpgradePlans';
import { useUserLocation, useConsumption } from '../../queries';
import { cn } from '../ui/utils';

/**
 * Upgrade CTA + paywall dialog.
 * The dialog must stay mounted on chat screens (button may be hidden) so
 * UpgradeBanner / credit-limit flows can still open the paywall via showPlans().
 */
const Upgrade = ({ showButton = true }) => {
  const { user, userSubscriptionPlan } = useAuth();
  const { isPricingPlansVisible, showPlans, hidePlans } = useApp();
  const { data: consumptionData } = useConsumption();
  useUserLocation();

  if (
    !user?.email ||
    !userSubscriptionPlan ||
    userSubscriptionPlan === SUBSCRIPTION_PLANS.PRO
  ) {
    return null;
  }

  const isCreditsExhausted = consumptionData?.credits <= 0;
  const title = isCreditsExhausted
    ? 'Daily free credits used up'
    : 'Upgrade to Classwise Pro';

  return (
    <>
      {showButton ? (
        <button
          type="button"
          onClick={showPlans}
          aria-label="Upgrade to Pro"
          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-3 text-sm font-semibold text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/40 focus-visible:ring-offset-2 max-[380px]:px-2.5"
        >
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
          <span className="max-[380px]:hidden">Upgrade to Pro</span>
          <span className="hidden max-[380px]:inline">Pro</span>
        </button>
      ) : null}

      <Dialog
        open={isPricingPlansVisible}
        onOpenChange={(open) => {
          if (!open) hidePlans();
        }}
      >
        <DialogContent
          className={cn(
            'flex flex-col gap-0 overflow-hidden border-border bg-white p-0',
            // Desktop centered modal — wide enough for plan cards in one row
            'md:max-h-[min(90vh,820px)] md:w-[min(100%-2rem,56rem)] md:max-w-4xl md:rounded-2xl',
            'md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95 md:duration-200',
            // Mobile bottom drawer
            'max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:left-0 max-md:h-auto max-md:max-h-[92dvh] max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-b-none max-md:rounded-t-3xl max-md:border-x-0 max-md:border-b-0',
            'max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom',
            'max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100',
            'max-md:data-[state=open]:duration-500 max-md:data-[state=closed]:duration-300 max-md:ease-out'
          )}
          showCloseButton={false}
        >
          <div className="relative shrink-0 border-b border-border">
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted md:hidden" aria-hidden />

            <div className="flex items-start gap-3 px-4 py-3 md:px-6 md:py-5">
              <div className="mt-0.5 hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 md:flex">
                <Sparkles className="h-5 w-5 text-primary" aria-hidden />
              </div>

              <div className="min-w-0 flex-1 text-left">
                <DialogTitle className="text-base font-bold leading-snug text-foreground md:text-lg">
                  {title}
                </DialogTitle>
                <DialogDescription className="mt-1 text-xs leading-relaxed text-muted-foreground md:text-sm">
                  Unlimited practice, doubt solving, and personalised training.
                </DialogDescription>
              </div>

              <DialogClose asChild>
                <Button
                  type="button"
                  buttonType="secondary"
                  aria-label="Close"
                  className="h-9 w-9 shrink-0 p-0"
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </DialogClose>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-6 md:py-5">
            <UpgradePlans closePlans={hidePlans} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Upgrade;
